/**
 * Storico locale dei report generati, per configurazione. La generazione del
 * report è client-side (nessun backend): la {@link ReportSpec} e la riga di
 * elenco vengono conservate in `localStorage` così sopravvivono a un refresh e
 * i pulsanti "apri"/"scarica" possono ricostruire il file.
 */
import type { GeneratedReportItem, ReportSpec } from '@slims/ui/organisms';
import { useCallback, useEffect, useState } from 'react';

export interface StoredReport {
	item: GeneratedReportItem;
	spec: ReportSpec;
}

const KEY_PREFIX = 'slims:reports:';
const MAX_KEPT = 50;

const ITALIAN_DATE = new Intl.DateTimeFormat('it-IT', {
	day: '2-digit',
	month: 'short',
	year: 'numeric',
});

function storageKey(orgId: string): string {
	return `${KEY_PREFIX}${orgId}`;
}

function read(orgId: string): StoredReport[] {
	if (typeof window === 'undefined') return [];
	try {
		const raw = window.localStorage.getItem(storageKey(orgId));
		const parsed = raw ? JSON.parse(raw) : null;
		return Array.isArray(parsed) ? (parsed as StoredReport[]) : [];
	} catch {
		return [];
	}
}

function write(orgId: string, reports: StoredReport[]): void {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.setItem(storageKey(orgId), JSON.stringify(reports));
	} catch {
		// quota piena o modalità privata: la persistenza è best-effort.
	}
}

function toItem(spec: ReportSpec): GeneratedReportItem {
	const blocks = spec.sections.reduce((n, s) => n + s.blocks.length, 0);
	const fmt = spec.format === 'excel' ? 'EXCEL' : 'PDF';
	return {
		id: `gen-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
		title: `${spec.title} - ${spec.periodLabel}`,
		sub: `${fmt} · ${blocks} blocchi · ${ITALIAN_DATE.format(new Date(spec.generatedAt))}`,
		tone: 'green',
		status: '✓ nuovo',
	};
}

export function useGeneratedReports(orgId: string) {
	const [reports, setReports] = useState<StoredReport[]>(() => read(orgId));

	// Cambio di configurazione in ambito → ricarica lo storico di quella.
	useEffect(() => {
		setReports(read(orgId));
	}, [orgId]);

	const add = useCallback(
		(spec: ReportSpec): StoredReport => {
			const entry: StoredReport = { item: toItem(spec), spec };
			setReports((prev) => {
				const next = [entry, ...prev].slice(0, MAX_KEPT);
				write(orgId, next);
				return next;
			});
			return entry;
		},
		[orgId],
	);

	const remove = useCallback(
		(id: string) => {
			setReports((prev) => {
				const next = prev.filter((r) => r.item.id !== id);
				write(orgId, next);
				return next;
			});
		},
		[orgId],
	);

	const find = useCallback(
		(id: string): StoredReport | undefined => reports.find((r) => r.item.id === id),
		[reports],
	);

	return { reports, add, remove, find };
}
